/** Send a claim link to one of the partner's active technical contacts (react-hook-form +
 *  zod; the first contact is preselected). This is the hand-over, and also how a key is
 *  rotated. A refusal (409: the partner may not hold keys now, has no sandbox, or already
 *  holds two keys; 503: the e-mail could not be sent and the link was revoked) stays in the
 *  dialog with the backend's own words. Audited `partner.claim_link_sent`. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { sendClaimLink, type PartnerDetail } from '@/api/partners';
import { AppDialog } from '@/components/AppDialog';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';

import { partnerErrorMessage } from './partnerErrors';

const Schema = z.object({
  contact_id: z.number({ invalid_type_error: 'Pick a technical contact' }).int().positive('Pick a technical contact'),
});

type Values = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  partner: PartnerDetail;
  onClose: () => void;
}

export function SendClaimLinkDialog({ open, partner, onClose }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const contacts = useMemo(
    () => partner.contacts.filter((c) => c.role === 'technical' && c.is_active),
    [partner.contacts],
  );
  const activeKeys = partner.developer_keys.filter((k) => k.status === 'active').length;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(Schema), defaultValues: { contact_id: contacts[0]?.id ?? 0 } });

  const mutation = useMutation({
    mutationFn: (v: Values) => sendClaimLink(partner.id, { contact_id: v.contact_id }),
    onSuccess: (link) => {
      snackbar.show(`Claim link sent to ${link.contact?.email ?? 'the contact'}.`, 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
      onClose();
    },
  });
  const { reset: resetMutation } = mutation;

  // Seed on open only: a background refetch of the partner must not undo the pick.
  useEffect(() => {
    if (!open) return;
    reset({ contact_id: contacts[0]?.id ?? 0 });
    resetMutation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Send claim link — {partner.name}
          <HelpLink story="partner-onboarding" newTab />
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mutation.isPending || contacts.length === 0}
            onClick={handleSubmit((v) => mutation.mutate(v))}
          >
            Send claim link
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <Typography variant="body2">
          The contact receives a one-time link, valid 72 hours. Confirming it on that page mints the
          partner’s developer key and shows it to the contact <strong>once</strong> — nobody here ever
          sees it. Any pending link of this partner is revoked.
        </Typography>
        {activeKeys === 1 && (
          <Alert severity="info">
            {partner.name} already holds an active key. A new link is how a key is rotated: a second key is
            allowed during rotation, and once it is claimed the older key keeps working for 7 more days.
          </Alert>
        )}
        {activeKeys >= 2 && (
          <Alert severity="warning">
            {partner.name} already holds two active keys, the most it may have: a new link is refused until one
            of them is revoked (on its API credential) or expires.
          </Alert>
        )}
        {contacts.length === 0 ? (
          <Alert severity="warning">
            No active technical contact. Add one (or reactivate one) under Contacts first.
          </Alert>
        ) : (
          <Controller
            name="contact_id"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Technical contact"
                value={field.value || ''}
                onChange={(e) => field.onChange(Number(e.target.value))}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={!!errors.contact_id}
                helperText={errors.contact_id?.message ?? 'Only active technical contacts can receive a claim link.'}
              >
                {contacts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} &lt;{c.email}&gt;
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}
        {mutation.isError && (
          <Alert severity="error">{partnerErrorMessage(mutation.error, 'The claim link could not be sent.')}</Alert>
        )}
      </Box>
    </AppDialog>
  );
}
